import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { ModalBase } from "./modal-base";
import { registerModal, modalContext } from "../store/modal-store";
import { productData } from "../store/product-store";

/**
 * Confirm Custom Text Modal
 *
 * Shows a confirmation dialog when adding a product with custom text/personalization.
 * Validates that all mandatory fields are filled in and displays a preview.
 *
 * Usage in Liquid:
 * <confirm-custom-text-modal
 *   data-na-text="N/A"
 *   data-mandatory-message="Please fill in all required customization fields."
 * ></confirm-custom-text-modal>
 */
@customElement("confirm-custom-text-modal")
export class ConfirmCustomTextModal extends ModalBase {
  @property({ attribute: "data-na-text" }) naText = "N/A";
  @property({ attribute: "data-mandatory-message" }) mandatoryMessage =
    "Please fill in all required customization fields before adding to cart.";

  @state() private _customizationData: Record<string, string> = {};
  @state() private _hasInvalidFields = false;
  @state() private _missingFields: string[] = [];

  constructor() {
    super();
    this.name = "confirm-custom-text";
    this.allowOutsideClick = false; // Force user to make a choice

    // Register with modal orchestrator
    registerModal({
      name: "confirm-custom-text",
      priority: 20, // After holiday shipping warning (priority 10)
      shouldShow: (context) => {
        // The <custom-text> elements only exist on the main product page, so this
        // modal must only fire for the main product CTA. Collection cards (e.g.
        // recommended products) share this modal chain — without this guard the
        // main product's custom text would be confirmed and injected into an
        // unrelated card's cart line item via onApprove.
        if (
          context.productId === undefined ||
          context.productId !== productData.value?.id
        ) {
          return false;
        }

        // Check if product has custom text elements
        const customTextElements = document.querySelectorAll("custom-text");
        if (customTextElements.length === 0) return false;

        // Validate custom text inputs
        const validation = this._validateCustomText();
        this._hasInvalidFields = validation.hasInvalid;
        this._missingFields = validation.missingFields;
        this._customizationData = validation.data;

        // Show modal if there are custom fields (either to confirm or to warn about missing)
        return true;
      },
      onApprove: (context) => {
        // Add customization to context
        if (!context.customization) {
          context.customization = {};
        }
        Object.assign(context.customization, this._customizationData);
        console.log(
          "[confirm-custom-text] Approved with customization:",
          this._customizationData
        );
      },
      onCancel: (context) => {
        console.log("[confirm-custom-text] User cancelled");
      },
    });

    // Subscribe to modal context for reactivity
    effect(() => {
      if (modalContext.value && this._isOpen) {
        // Re-validate when modal opens
        const validation = this._validateCustomText();
        this._hasInvalidFields = validation.hasInvalid;
        this._missingFields = validation.missingFields;
        this._customizationData = validation.data;
      }
    });
  }

  /**
   * Validate custom text inputs and return validation result
   */
  private _validateCustomText(): {
    hasInvalid: boolean;
    missingFields: string[];
    data: Record<string, string>;
  } {
    const customTextElements = document.querySelectorAll("custom-text");
    const data: Record<string, string> = {};
    const missingFields: string[] = [];

    customTextElements.forEach((element) => {
      const label =
        element.getAttribute("data-label") ||
        element.querySelector("label")?.textContent?.trim() ||
        "Custom Text";
      const isMandatory = element.hasAttribute("data-mandatory");
      const input = element.querySelector("input, textarea") as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null;

      if (input) {
        const value = input.value.trim();
        data[label] = value;

        if (isMandatory && !value) {
          missingFields.push(label);
        }
      }
    });

    return {
      hasInvalid: missingFields.length > 0,
      missingFields,
      data,
    };
  }

  protected override handleApprove() {
    // Only allow approval if all mandatory fields are filled
    if (this._hasInvalidFields) {
      console.warn(
        "[confirm-custom-text] Cannot approve: missing mandatory fields"
      );
      return;
    }
    super.handleApprove();
  }

  render() {
    if (!this._isOpen) return html``;

    const hasCustomization = Object.keys(this._customizationData).length > 0;

    return html`
      <div
        class="modal-overlay fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-custom-text-title"
      >
        <div
          class="modal-dialog bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="p-6">
            <h2
              id="confirm-custom-text-title"
              class="text-xl font-bold text-gray-900 mb-4"
            >
              Confirm Your Customization
            </h2>

            ${this._hasInvalidFields
              ? html`
                  <div
                    class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
                  >
                    <div class="flex items-start">
                      <svg
                        class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div class="flex-1">
                        <h3 class="text-sm font-medium text-red-800 mb-1">
                          Missing Required Fields
                        </h3>
                        <p class="text-sm text-red-700 mb-2">
                          ${this.mandatoryMessage}
                        </p>
                        <ul class="list-disc list-inside text-sm text-red-700">
                          ${this._missingFields.map(
                            (field) => html`<li>${field}</li>`
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                `
              : ""}

            <div class="space-y-3 mb-6">
              ${hasCustomization
                ? Object.entries(this._customizationData).map(
                    ([label, value]) => html`
                      <div
                        class="border-b border-gray-200 pb-2 last:border-b-0"
                      >
                        <div class="text-sm font-medium text-gray-700 mb-1">
                          ${label}
                        </div>
                        <div class="text-sm text-gray-900">
                          ${value || html`<span class="text-gray-400">${this.naText}</span>`}
                        </div>
                      </div>
                    `
                  )
                : html`
                    <p class="text-sm text-gray-600">
                      No customization fields found.
                    </p>
                  `}
            </div>

            <div class="flex flex-col gap-3">
              ${this._hasInvalidFields
                ? html`
                    <button
                      class="btn-secondary w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      @click=${this.handleCancel}
                      type="button"
                    >
                      Go Back and Edit
                    </button>
                  `
                : html`
                    <button
                      class="btn-primary w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      @click=${this.handleApprove}
                      type="button"
                    >
                      Confirm and Add to Cart
                    </button>
                    <button
                      class="btn-secondary w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      @click=${this.handleCancel}
                      type="button"
                    >
                      Edit Customization
                    </button>
                  `}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
